import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Badge,
  ListGroup,
  ListGroupItem,
  Collapse,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCube,
  faCheck,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { preProductionApi } from "../../../../services/preProductionServices";
import Loader from "../../../Loader";
import { useJobDetails } from "../../../../providers/jobDetailsProvider";

const DONE_STATUS_ID = 3;

const PreProductionSummary = () => {
  const [jobDetails] = useJobDetails();
  const { job } = jobDetails;
  const jobId = job?.id;
  const [preProductionItems, setPreProductionItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    const fetchPreProductionItems = async () => {
      if (jobId) {
        setLoading(true);
        try {
          const response = await preProductionApi.getAllPreProductionItems({
            jobId,
          });
          if (response.data && response.data.length > 0) {
            setPreProductionItems(
              response.data[response.data.length - 1].preProductionItems || []
            );
          } else {
            setPreProductionItems([]);
          }
        } catch (error) {
          console.error("Error fetching pre-production info", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchPreProductionItems();
  }, [jobId]);

  const { doneItems, totalItems } = useMemo(() => {
    return {
      doneItems: preProductionItems.filter(
        (item) => item.preProductionStatus.id === DONE_STATUS_ID
      ).length,
      totalItems: preProductionItems.length,
    };
  }, [preProductionItems]);

  const getStatusBadge = (preProductionStatus) => {
    const isDone = preProductionStatus.id === DONE_STATUS_ID;
    const badgeClass = `p-2 preprod-badge ${
      isDone ? "preprod-badge-done" : "preprod-badge-info"
    }`;
    const dotClass = `preprod-status-dot ${
      isDone ? "preprod-status-dot-done" : "preprod-status-dot-info"
    }`;

    const content = isDone ? (
      <>
        <FontAwesomeIcon icon={faCheck} className="mr-1" />
        {` ${preProductionStatus.name}`}
      </>
    ) : (
      <>
        <span className={dotClass} />
        {` ${preProductionStatus.name}`}
      </>
    );

    return (
      <Badge pill color="light" className={badgeClass}>
        {content}
      </Badge>
    );
  };

  if (!jobId) {
    return null;
  }

  return (
    <Card className="shadow-sm border-0 m-0 p-3">
      <CardHeader className="bg-white border-0 p-0">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div className="rounded-circle d-flex align-items-center justify-content-center p-2 bg-soft-blue">
              <FontAwesomeIcon icon={faCube} size="lg" />
            </div>
            <h4 className="mb-0 ml-3 font-weight-bold">Pre-Production</h4>
          </div>
          <div className="d-flex align-items-center">
            <h4 className="mb-0 font-weight-bold">
              {doneItems}/{totalItems}
            </h4>
            <button
              type="button"
              className="btn btn-link text-dark p-0 ml-3"
              onClick={() => setIsCollapsed((prev) => !prev)}
              aria-label={
                isCollapsed
                  ? "Expand pre-production"
                  : "Collapse pre-production"
              }
            >
              <FontAwesomeIcon
                icon={isCollapsed ? faChevronDown : faChevronUp}
                size="lg"
              />
            </button>
          </div>
        </div>
      </CardHeader>
      <Collapse isOpen={!isCollapsed}>
        {loading ? (
          <CardBody className="d-flex">
            <Loader size={"sm"} />
          </CardBody>
        ) : (
          <CardBody className="p-0">
            <ListGroup flush className="h-100">
              {preProductionItems.length > 0 ? (
                preProductionItems.map((item) => (
                  <ListGroupItem
                    key={item.id}
                    className="d-flex justify-content-between align-items-center border-0 px-0 pb-0 pt-2"
                  >
                    <span>{item.preProductionItemType.name}</span>
                    {getStatusBadge(item.preProductionStatus)}
                  </ListGroupItem>
                ))
              ) : (
                <ListGroupItem className="d-flex justify-content-center align-items-center border-0 px-0 pb-0 pt-2 h-100">
                  <span>No pre-production items for this Job yet</span>
                </ListGroupItem>
              )}
            </ListGroup>
          </CardBody>
        )}
      </Collapse>
    </Card>
  );
};

export default PreProductionSummary;
